import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  AlertTriangle,
  Globe,
  RefreshCw,
  Loader2,
  Zap,
  MapPin,
  Clock,
  ExternalLink,
  Bell,
  Shield,
  Flame,
  Cloud,
  Building2,
} from "lucide-react";

const THREAT_CATEGORIES = {
  natural_disaster: { icon: Cloud, color: "text-blue-600", bg: "bg-blue-100" },
  cyber_attack: { icon: Shield, color: "text-purple-600", bg: "bg-purple-100" },
  civil_unrest: { icon: Building2, color: "text-sky-700", bg: "bg-sky-100" },
  health_emergency: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-100" },
  infrastructure: { icon: Zap, color: "text-amber-600", bg: "bg-amber-100" },
  geopolitical: { icon: Globe, color: "text-slate-600", bg: "bg-slate-100" },
  fire: { icon: Flame, color: "text-red-600", bg: "bg-red-100" },
};

export default function ThreatIntelligencePanel({ locations = [] }) {
  const { api, user } = useAuth();
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (locations.length > 0) {
      fetchThreatIntelligence();
    }
  }, [locations]);

  const fetchThreatIntelligence = async () => {
    setLoading(true);
    try {
      const response = await api.post("/ai-assistant/threat-intelligence", {
        locations: locations.map(l => ({
          name: l.name,
          address: l.address,
          latitude: l.latitude,
          longitude: l.longitude
        })),
        org_id: user?.org_id
      });
      
      if (response.data?.success) {
        setThreats(response.data.threats || []);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error("Failed to fetch threat intelligence:", error);
      // Use simulated threats for demo
      setThreats(generateSimulatedThreats(locations));
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  // Generate simulated threats for demonstration
  const generateSimulatedThreats = (locs) => {
    const simulated = [];
    
    locs.forEach(loc => {
      const address = loc.address?.toLowerCase() || '';
      
      // Location-based threat simulation
      if (address.includes('texas') || address.includes('tx')) {
        simulated.push({
          id: `threat-${Date.now()}-1`,
          category: "natural_disaster",
          title: "Severe Weather Advisory",
          description: "National Weather Service has issued a severe thunderstorm watch for the region. Potential for large hail and damaging winds.",
          affected_location: loc.name,
          severity: "medium",
          source: "National Weather Service",
          timestamp: new Date().toISOString(),
        });
      }
      
      if (address.includes('california') || address.includes('ca')) {
        simulated.push({
          id: `threat-${Date.now()}-2`,
          category: "fire",
          title: "Elevated Fire Risk",
          description: "Red flag warning in effect due to dry conditions and high winds. Fire weather watch for surrounding counties.",
          affected_location: loc.name,
          severity: "high",
          source: "CAL FIRE",
          timestamp: new Date().toISOString(),
        });
      }
      
      if (address.includes('florida') || address.includes('fl')) {
        simulated.push({
          id: `threat-${Date.now()}-3`,
          category: "natural_disaster",
          title: "Hurricane Season Advisory",
          description: "Active hurricane season continues. Monitor tropical developments in the Atlantic basin.",
          affected_location: loc.name,
          severity: "low",
          source: "National Hurricane Center",
          timestamp: new Date().toISOString(),
        });
      }
    });
    
    // Global cyber threat (always relevant)
    simulated.push({
      id: `threat-${Date.now()}-4`,
      category: "cyber_attack",
      title: "Increased Ransomware Activity",
      description: "CISA reports elevated ransomware activity targeting critical infrastructure sectors. Ensure systems are patched and backups are current.",
      affected_location: "All Locations",
      severity: "medium",
      source: "CISA",
      timestamp: new Date().toISOString(),
    });
    
    return simulated;
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "high":
        return <Badge className="bg-red-500 text-white">High</Badge>;
      case "medium":
        return <Badge className="bg-amber-500 text-white">Medium</Badge>;
      case "low":
        return <Badge className="bg-blue-500 text-white">Low</Badge>;
      default:
        return <Badge variant="outline">Info</Badge>;
    }
  };

  const getCategoryInfo = (category) => {
    return THREAT_CATEGORIES[category] || THREAT_CATEGORIES.geopolitical;
  };

  if (locations.length === 0) {
    return null;
  }

  return (
    <Card className="border-red-200 bg-gradient-to-br from-red-50 to-sky-50" data-testid="threat-intelligence-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Bell className="w-5 h-5 text-red-600" />
            AI Threat Intelligence
          </CardTitle>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchThreatIntelligence}
              disabled={loading}
              className="h-8 w-8 p-0"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Real-time monitoring of regional risks affecting your locations
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-red-600 mr-2" />
            <span className="text-slate-600">Scanning global threat feeds...</span>
          </div>
        ) : threats.length > 0 ? (
          <div className="space-y-3">
            {threats.map((threat) => {
              const categoryInfo = getCategoryInfo(threat.category);
              const CategoryIcon = categoryInfo.icon;
              
              return (
                <div
                  key={threat.id}
                  className="p-3 bg-white rounded-lg border border-red-100 hover:border-red-200 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${categoryInfo.bg}`}>
                      <CategoryIcon className={`w-4 h-4 ${categoryInfo.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900">
                          {threat.title}
                        </span>
                        {getSeverityBadge(threat.severity)}
                      </div>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                        {threat.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {threat.affected_location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          {threat.source}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <Shield className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-emerald-700">No Active Threats</p>
            <p className="text-xs text-slate-500 mt-1">
              All monitored locations are currently clear of significant threats.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
